import { useCallback, useEffect, useRef, useState } from 'react';
import { Empty, Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api, type DocumentSummary, type FormField, type SignDocumentInput } from './api.js';

/**
 * The merchant's legal documents: a list of fillable, self-contained pages, each
 * already filled in from the account's live data.
 *
 * The list comes from the server's registry, so this view names no document of
 * its own - the Merchant Services Agreement is the first, and the rest arrive as
 * registry entries with nothing to change here. Opening one loads its rendered
 * `<x-dc>` island into an iframe, wrapped in a shell that pulls the runtime
 * (React and the dc-runtime) from this origin's external `'self'` scripts - see
 * `frameDocument` below for why that shape, not the compiled bundle, is what
 * survives the panel's strict CSP. The island brings its own print-to-PDF, so
 * this view does almost nothing to it: it fetches the markup (with the session
 * token, which is why it arrives as text and is handed to the frame through
 * `srcdoc` rather than by URL), shows it large enough to read at 1:1, and offers
 * the two controls that matter - "Download PDF", which triggers the document's
 * own print, and a way back.
 */
export function DocumentsView() {
  const [open, setOpen] = useState<DocumentSummary | null>(null);
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .documents()
      .then((d) => live && setDocs(d.documents))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : 'Could not load your documents.'));
    return () => {
      live = false;
    };
  }, []);

  if (open) return <DocumentViewer doc={open} onBack={() => setOpen(null)} />;

  if (error) {
    return (
      <Note tone="danger" title="This page could not be loaded">
        <p style={{ margin: 0 }}>{error}</p>
      </Note>
    );
  }
  if (!docs) return <Spinner label="Loading…" />;
  if (docs.length === 0) return <Empty>No documents are available yet.</Empty>;

  // Grouped by category, in the order the registry first mentions each one.
  const groups: Array<{ category: string; items: DocumentSummary[] }> = [];
  for (const doc of docs) {
    let group = groups.find((g) => g.category === doc.category);
    if (!group) {
      group = { category: doc.category, items: [] };
      groups.push(group);
    }
    group.items.push(doc);
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Your documents">
        <p style={{ margin: 0 }}>
          These are filled in from your account and ready to read. Open one to review it, click any text to
          edit it in place, and use <b>Download PDF</b> for a clean, print-ready copy.
        </p>
      </Note>

      {groups.map((group) => (
        <Panel key={group.category} title={group.category}>
          <div style={{ display: 'grid', gap: 10 }}>
            {group.items.map((doc) => (
              <button
                key={doc.key}
                type="button"
                onClick={() => setOpen(doc)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--wc-blue)';
                  e.currentTarget.style.background = 'var(--wc-soft)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--wc-border)';
                  e.currentTarget.style.background = 'transparent';
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  width: '100%',
                  textAlign: 'left',
                  padding: '14px 16px',
                  border: '1px solid var(--wc-border)',
                  borderRadius: 10,
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <span style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--wc-ink)' }}>{doc.title}</span>
                  <span style={{ fontSize: 13, color: 'var(--wc-muted)', lineHeight: 1.5 }}>
                    {doc.description}
                  </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
                  <StateBadge state={doc.state} />
                  <span style={{ color: 'var(--wc-faint)', fontSize: 13, fontWeight: 600 }}>Open →</span>
                </span>
              </button>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  );
}

/**
 * Wrap the server's `<x-dc>` island in the document the iframe actually loads.
 *
 * The server returns just the island, with runtime URLs left as a literal
 * `__RUNTIME_BASE__` placeholder (see the API's documents/render.ts). Here that
 * placeholder becomes this origin's `/documents/runtime`, and the island is
 * dropped into a shell whose head pulls in the runtime as three external,
 * same-origin scripts - React, then ReactDOM, then `support.js`. Order matters:
 * `support.js` needs `window.React`/`window.ReactDOM` present, and on
 * DOMContentLoaded it mounts the island in the body and relocates the helmet's
 * `./doc-page.js` (now absolute) into the head itself.
 *
 * Every executable script here is an absolute `'self'` URL and there is no
 * inline script, so the shell passes the merchant panel's strict CSP
 * (`script-src 'self' …`, no `'unsafe-inline'`, no `blob:`) - which the compiled
 * bundle's inline React and blob: rehydration did not, and why it was blank on
 * prod. `srcdoc` inherits that same policy, so this is the whole point.
 */
function frameDocument(island: string): string {
  const base = `${window.location.origin}/documents/runtime`;
  const body = island.split('__RUNTIME_BASE__').join(base);
  return (
    '<!DOCTYPE html><html lang="en"><head>' +
    '<meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">' +
    `<script src="${base}/react.production.min.js"></script>` +
    `<script src="${base}/react-dom.production.min.js"></script>` +
    `<script src="${base}/support.js"></script>` +
    `</head><body>${body}</body></html>`
  );
}

/** A small coloured chip showing a document's signing state in the list. */
function StateBadge({ state }: { state?: DocumentSummary['state'] }) {
  if (!state || state === 'preview') return null;
  const map = {
    accepted: { label: 'Signed', bg: 'var(--wc-ok-soft, #e7f6ec)', fg: 'var(--wc-ok, #1a7f4b)' },
    issued: { label: 'To sign', bg: 'var(--wc-warn-soft, #fdf3e3)', fg: 'var(--wc-warn, #9a6b00)' },
    declined: { label: 'Declined', bg: 'var(--wc-danger-soft, #fdecec)', fg: 'var(--wc-danger, #b42525)' },
  } as const;
  const s = map[state];
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.03em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
      }}
    >
      {s.label}
    </span>
  );
}

/** One open document, filling the content area, with its own print as the PDF. */
function DocumentViewer({ doc, onBack }: { doc: DocumentSummary; onBack: () => void }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<NonNullable<DocumentSummary['state']>>(doc.state ?? 'preview');
  const [number, setNumber] = useState<string | null>(doc.number ?? null);
  const [acceptedAt, setAcceptedAt] = useState<string | null>(doc.acceptedAt ?? null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  const loadHtml = useCallback(() => {
    let live = true;
    setHtml(null);
    setError(null);
    api
      .documentHtml(doc.key)
      .then((markup) => live && setHtml(frameDocument(markup)))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : 'The document could not be loaded.'));
    return () => {
      live = false;
    };
  }, [doc.key]);

  useEffect(() => loadHtml(), [loadHtml]);

  // Fillable documents (bank change, due diligence) ask the merchant to complete
  // some fields; fetch those and their current values so a form can be shown.
  const [form, setForm] = useState<{ fields: FormField[]; values: Record<string, string> } | null>(null);
  useEffect(() => {
    let live = true;
    api
      .documentForm(doc.key)
      .then((f) => live && setForm(f))
      .catch(() => live && setForm({ fields: [], values: {} }));
    return () => {
      live = false;
    };
  }, [doc.key]);
  const hasForm = (form?.fields.length ?? 0) > 0;

  // A signed document downloads as its digitally-signed PDF, generated and
  // PAdES-signed server-side. If that is unavailable, it falls back to the
  // browser's own print of the frame.
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await api.documentSignedPdf(doc.key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number ?? doc.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'pdf_failed' || e.status === 400)) {
        const win = frameRef.current?.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } else {
        setError(e instanceof ApiError ? e.message : 'The PDF could not be downloaded.');
      }
    } finally {
      setDownloading(false);
    }
  };

  // The court-facing proof: the signature, the certificate and the exact hash,
  // plus how to verify them with openssl - saved as a plain-text file.
  const downloadProof = async () => {
    try {
      const p = await api.documentSeal(doc.key);
      const content = [
        'WyoCSPay - Document Cryptographic Seal (verification proof)',
        '',
        `Document:            ${doc.title}`,
        `Reference:           ${p.number}`,
        `Seal authority:      ${p.signer}`,
        `Algorithm:           ${p.algorithm}`,
        `Signed doc SHA-256:  ${p.sha256}`,
        `Sealed at (UTC):     ${p.sealedAt ?? '-'}`,
        `Accepted at (UTC):   ${p.acceptedAt ?? '-'}`,
        `Server re-check:     ${p.verified ? 'VALID' : 'FAILED'}`,
        '',
        '--- SIGNATURE (base64, over the exact signed document bytes) ---',
        p.signature,
        '',
        '--- CERTIFICATE (PEM) ---',
        p.certificate.trim(),
        '',
        '--- HOW TO VERIFY INDEPENDENTLY ---',
        '1) Save the base64 signature above to signature.b64, then:  base64 -d signature.b64 > signature.bin',
        '2) Save the certificate block above to certificate.pem',
        '3) openssl x509 -in certificate.pem -pubkey -noout > pubkey.pem',
        '4) openssl dgst -sha256 -verify pubkey.pem -signature signature.bin <the signed document bytes>',
        '   Expected: "Verified OK". The document bytes are the signed page exactly as served.',
      ].join('\n');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${p.number}-signature-proof.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The signature proof could not be loaded.');
    }
  };

  const signable = !!doc.signable && (state === 'preview' || state === 'issued');

  // The due-diligence record is internal: the merchant completes only their own
  // business profile and does not see the risk assessment or decision. Show the
  // form alone, not the full document.
  if (doc.internal) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack}>
            ← Back to documents
          </button>
          <div style={{ flex: '1 1 auto', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--wc-ink)' }}>
            {doc.title}
          </div>
          <span style={{ width: 130 }} />
        </div>
        <Note>
          Complete your business profile below. WyoCSPay Risk and Compliance completes the assessment and
          decision; this is an internal record kept for your account.
        </Note>
        {form && hasForm ? (
          <FormEditor
            docKey={doc.key}
            fields={form.fields}
            values={form.values}
            onSaved={() => undefined}
            submitLabel="Save business profile"
          />
        ) : (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
            <Spinner label="Loading…" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 'calc(100vh - 210px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack}>
          ← Back to documents
        </button>
        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            textAlign: 'center',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--wc-ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.title}
        </div>
        {state === 'accepted' ? (
          <button type="button" className="wc-btn wc-btn-sm" onClick={download} disabled={!html || downloading}>
            {downloading ? 'Preparing PDF…' : doc.platformIssued ? 'Download certificate' : 'Download signed PDF'}
          </button>
        ) : signable ? (
          <span style={{ fontSize: 12, color: 'var(--wc-faint)' }}>Download available after signing</span>
        ) : null}
      </div>

      {state === 'accepted' ? (
        doc.platformIssued ? null : (
          <Note tone="ok" title="Signed">
            <p style={{ margin: '0 0 10px' }}>
              You signed this document{number ? <> - reference <b>{number}</b></> : null}
              {acceptedAt ? <> on {new Date(acceptedAt).toLocaleString()}</> : null}. The signed copy below is
              final and carries a cryptographic seal from Wyoming Cloud Solutions LLC, so its authenticity can
              be verified independently.
            </p>
            <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={downloadProof}>
              Download signature proof
            </button>
          </Note>
        )
      ) : state === 'declined' ? (
        <Note tone="danger" title="Declined">
          <p style={{ margin: 0 }}>
            {doc.declineCloses
              ? 'This agreement was declined and the account has been closed. This cannot be undone.'
              : 'This document was declined. You can raise it again if you still need it.'}
          </p>
        </Note>
      ) : signable ? (
        <SignFlow
          docKey={doc.key}
          reissued={state === 'issued' && doc.reason === 'fee_change'}
          declineCloses={doc.declineCloses}
          onSigned={(num, at) => {
            setState('accepted');
            setNumber(num);
            setAcceptedAt(at);
            loadHtml();
          }}
          onDeclined={() => {
            setState('declined');
            loadHtml();
          }}
        />
      ) : null}

      {hasForm && state !== 'accepted' && form ? (
        <FormEditor docKey={doc.key} fields={form.fields} values={form.values} onSaved={loadHtml} submitLabel="Save details" />
      ) : null}

      {error ? (
        <Note tone="danger" title="This document could not be loaded">
          <p style={{ margin: 0 }}>{error}</p>
        </Note>
      ) : !html ? (
        <div style={{ display: 'grid', placeItems: 'center', flex: '1 1 auto', minHeight: 320 }}>
          <Spinner label="Preparing your document…" />
        </div>
      ) : (
        <iframe
          ref={frameRef}
          title={doc.title}
          srcDoc={html}
          style={{
            flex: '1 1 auto',
            width: '100%',
            minHeight: 'calc(100vh - 260px)',
            border: '1px solid var(--wc-border)',
            borderRadius: 10,
            background: '#fff',
          }}
        />
      )}

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.5 }}>
        <b>Download PDF</b> opens your browser's print dialog - choose “Save as PDF” for a 1:1 copy.
      </p>
    </div>
  );
}

/** Group fields by their section heading, preserving first-seen order. */
function groupFields(fields: FormField[]): Array<{ section: string; fields: FormField[] }> {
  const groups: Array<{ section: string; fields: FormField[] }> = [];
  for (const f of fields) {
    const section = f.section ?? '';
    let g = groups.find((x) => x.section === section);
    if (!g) {
      g = { section, fields: [] };
      groups.push(g);
    }
    g.fields.push(f);
  }
  return groups;
}

/**
 * A generic editor for a document's fillable fields, grouped by section. Text,
 * date and multi-line inputs plus single-choice dropdowns. Saves to the server
 * and calls back so the document preview can refresh with the entered values.
 * The same component serves the merchant's fields and (in the admin) the
 * reviewer's - it renders whatever field list it is handed.
 */
function FormEditor({
  docKey,
  fields,
  values,
  onSaved,
  submitLabel,
}: {
  docKey: string;
  fields: FormField[];
  values: Record<string, string>;
  onSaved: () => void;
  submitLabel: string;
}) {
  const [data, setData] = useState<Record<string, string>>(values);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (token: string, value: string) => {
    setData((d) => ({ ...d, [token]: value }));
    setSaved(false);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.saveDocumentForm(docKey, data);
      setData(res.values);
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Your details could not be saved.');
    } finally {
      setBusy(false);
    }
  };
  const input: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--wc-border)',
    borderRadius: 8,
    fontSize: 13.5,
    background: 'var(--wc-bg, #fff)',
    color: 'var(--wc-ink)',
  };
  const label: React.CSSProperties = {
    fontSize: 11.5,
    fontWeight: 700,
    color: 'var(--wc-muted)',
    marginBottom: 4,
    display: 'block',
    letterSpacing: '.02em',
  };
  return (
    <Panel title="Complete the details">
      <div style={{ display: 'grid', gap: 16 }}>
        {groupFields(fields).map((g) => (
          <div key={g.section}>
            {g.section ? (
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--wc-ink)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                {g.section}
              </div>
            ) : null}
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {g.fields.map((f) => (
                <div key={f.token} style={{ gridColumn: f.span === 2 ? '1 / -1' : undefined }}>
                  <label style={label}>{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea style={{ ...input, minHeight: 60, resize: 'vertical' }} value={data[f.token] ?? ''} onChange={(e) => set(f.token, e.target.value)} />
                  ) : f.type === 'radio' || f.type === 'select' ? (
                    <select style={input} value={data[f.token] ?? ''} onChange={(e) => set(f.token, e.target.value)}>
                      <option value="">-</option>
                      {(f.options ?? []).map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input style={input} type={f.type === 'date' ? 'date' : 'text'} value={data[f.token] ?? ''} placeholder={f.placeholder} onChange={(e) => set(f.token, e.target.value)} />
                  )}
                  {f.help ? <div style={{ fontSize: 11, color: 'var(--wc-faint)', marginTop: 3 }}>{f.help}</div> : null}
                </div>
              ))}
            </div>
          </div>
        ))}
        {error ? <Note tone="danger">{error}</Note> : null}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="button" className="wc-btn wc-btn-sm" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : submitLabel}
          </button>
          {saved ? <span style={{ fontSize: 12.5, color: 'var(--wc-ok, #1c7a43)' }}>Saved</span> : null}
        </div>
      </div>
    </Panel>
  );
}

/**
 * The sign-or-decline flow for an agreement. The merchant fills the signature
 * block, signs by drawing or typing, and either accepts (which records the
 * Electronic Acceptance Record) or declines (which permanently closes the
 * account, confirmed first). The signature never leaves the browser as anything
 * but a self-contained data: URL.
 */
function SignFlow({
  docKey,
  reissued,
  declineCloses,
  onSigned,
  onDeclined,
}: {
  docKey: string;
  reissued?: boolean;
  declineCloses?: boolean;
  onSigned: (number: string, acceptedAt: string | null) => void;
  onDeclined: () => void;
}) {
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDecline, setConfirmDecline] = useState(false);

  const ready = name.trim().length >= 2 && !!signature;

  const sign = async () => {
    if (!ready || !signature) return;
    setBusy(true);
    setError(null);
    try {
      const method: SignDocumentInput['method'] = signature.startsWith('data:image/svg')
        ? 'electronic'
        : 'handwritten';
      const res = await api.signDocument(docKey, {
        name: name.trim(),
        title: title.trim() || undefined,
        email: email.trim() || undefined,
        signature,
        method,
      });
      onSigned(res.number, res.accepted_at);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The agreement could not be signed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.declineDocument(docKey);
      onDeclined();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The request could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const input: React.CSSProperties = {
    width: '100%',
    padding: '9px 11px',
    border: '1px solid var(--wc-border)',
    borderRadius: 8,
    fontSize: 14,
    background: 'var(--wc-bg, #fff)',
    color: 'var(--wc-ink)',
  };
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--wc-muted)', marginBottom: 4, display: 'block' };

  return (
    <Panel title={reissued ? 'Terms changed - review and re-sign' : 'Review and sign'}>
      <div style={{ display: 'grid', gap: 14 }}>
        {reissued ? (
          <Note tone="warn" title="Your terms have changed">
            The fees or terms on your account changed, so a new version of the agreement has been issued.
            The changes are <b>highlighted in yellow</b> in the document below. Review them and sign again to
            continue; the previous signed version stays on record.
          </Note>
        ) : null}
        <Note>
          Read the document below, then sign to accept it. Your typed name, signature, the date, your IP
          address and this device are recorded as your Electronic Acceptance. Once signed, the copy is final.
        </Note>

        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div>
            <label style={label}>Full name (as signing) *</label>
            <input style={input} value={name} maxLength={120} placeholder="e.g. Nikola Chakmakov" onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label style={label}>Title</label>
            <input style={input} value={title} maxLength={80} placeholder="e.g. Owner, Director" onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label style={label}>Email</label>
            <input style={input} value={email} maxLength={200} placeholder="you@business.com" onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        <div>
          <label style={label}>Signature *</label>
          <SignaturePad name={name} onChange={setSignature} />
        </div>

        {error ? <Note tone="danger">{error}</Note> : null}

        {confirmDecline ? (
          declineCloses ? (
            <Note tone="danger" title="Declining permanently closes your account">
              <p style={{ margin: '0 0 10px' }}>
                If you decline, your WyoCSPay account is <b>permanently deactivated and cannot be restored</b>,
                because the terms are a condition of continued service and this change is outside Wyoming Cloud
                Solutions LLC’s control. Are you sure?
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="wc-btn wc-btn-danger wc-btn-sm" onClick={decline} disabled={busy}>
                  {busy ? 'Declining…' : 'Yes, decline and close my account'}
                </button>
                <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => setConfirmDecline(false)} disabled={busy}>
                  Cancel
                </button>
              </div>
            </Note>
          ) : (
            <Note tone="warn" title="Decline this document?">
              <p style={{ margin: '0 0 10px' }}>
                This document will be recorded as declined. Your account stays active and you can raise it
                again later if you need it.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="wc-btn wc-btn-danger wc-btn-sm" onClick={decline} disabled={busy}>
                  {busy ? 'Declining…' : 'Yes, decline'}
                </button>
                <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => setConfirmDecline(false)} disabled={busy}>
                  Cancel
                </button>
              </div>
            </Note>
          )
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="wc-btn" onClick={sign} disabled={busy || !ready}>
              {busy ? 'Signing…' : 'I agree and sign'}
            </button>
            <button type="button" className="wc-btn wc-btn-secondary" onClick={() => setConfirmDecline(true)} disabled={busy}>
              I decline
            </button>
          </div>
        )}
      </div>
    </Panel>
  );
}

/**
 * Capture a signature as a self-contained data: URL - drawn on a canvas, or
 * typed and rendered as a script-font SVG. Either way the value is an image the
 * document embeds directly; nothing is uploaded anywhere.
 */
/** A drawn signature as a PNG data URL cropped tight to the ink, so it fills the
 *  document's signature line instead of shrinking to nothing. */
function trimmedSignature(canvas: HTMLCanvasElement): string {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.toDataURL('image/png');
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width, minY = height, maxX = 0, maxY = 0, found = false;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 12) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return canvas.toDataURL('image/png');
  const pad = 10;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d')?.drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
  return out.toDataURL('image/png');
}

function SignaturePad({ name, onChange }: { name: string; onChange: (dataUrl: string | null) => void }) {
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const startDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const ctx = c.getContext('2d')!;
    const p = point(e);
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    c.setPointerCapture(e.pointerId);
  };
  const moveDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = '#1e46a8';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    dirty.current = true;
  };
  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current) onChange(trimmedSignature(canvasRef.current!));
  };
  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  };

  // Type mode: turn the entered name into a script-font SVG signature. The SVG
  // is sized to the measured text width so no name is ever clipped, however long.
  useEffect(() => {
    if (mode !== 'type') return;
    const text = name.trim();
    if (!text) {
      onChange(null);
      return;
    }
    const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const family = "'Brush Script MT','Segoe Script','Snell Roundhand',cursive";
    let width = text.length * 30 + 32; // fallback if measuring is unavailable
    const ctx = document.createElement('canvas').getContext('2d');
    if (ctx) {
      ctx.font = `46px ${family}`;
      width = Math.ceil(ctx.measureText(text).width) + 32;
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="72"><text x="14" y="52" font-family="${family}" font-size="46" fill="#1e46a8">${safe}</text></svg>`;
    onChange('data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg))));
  }, [mode, name, onChange]);

  const tab = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    border: '1px solid var(--wc-border)',
    borderBottom: active ? '2px solid var(--wc-blue, #1e46a8)' : '1px solid var(--wc-border)',
    borderRadius: '8px 8px 0 0',
    background: active ? 'var(--wc-soft, #f3f6fc)' : 'transparent',
    color: active ? 'var(--wc-blue, #1e46a8)' : 'var(--wc-muted)',
    cursor: 'pointer',
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: -1 }}>
        <button type="button" style={tab(mode === 'draw')} onClick={() => { setMode('draw'); onChange(dirty.current ? trimmedSignature(canvasRef.current!) : null); }}>
          Draw
        </button>
        <button type="button" style={tab(mode === 'type')} onClick={() => setMode('type')}>
          Type
        </button>
      </div>

      {mode === 'draw' ? (
        <div style={{ border: '1px solid var(--wc-border)', borderRadius: '0 8px 8px 8px', background: '#fff', padding: 8 }}>
          <canvas
            ref={canvasRef}
            width={640}
            height={150}
            onPointerDown={startDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
            style={{ width: '100%', height: 150, touchAction: 'none', cursor: 'crosshair', display: 'block', borderBottom: '1px solid var(--wc-border)' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--wc-faint)' }}>Sign with a mouse, trackpad or finger.</span>
            <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={clear}>
              Clear
            </button>
          </div>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--wc-border)', borderRadius: '0 8px 8px 8px', background: '#fff', padding: 16, minHeight: 90, display: 'flex', alignItems: 'center' }}>
          {name.trim() ? (
            <span style={{ fontFamily: "'Brush Script MT','Segoe Script','Snell Roundhand',cursive", fontSize: 40, color: '#1e46a8', lineHeight: 1 }}>
              {name.trim()}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: 'var(--wc-faint)' }}>Type your full name above and it becomes your signature here.</span>
          )}
        </div>
      )}
    </div>
  );
}
